/* Checkout upsell popup: when the shopper taps Checkout, offer the next Buy 2 / 3 / 4 step with kits that match the
   kit in the cart. The discount is the store's automatic "Buy 2/3/4" discount (wpCartConfig.tiers), nothing new.
   Shown when: 1-3 kits in the cart, no custom kit, not shown yet this visit, no "No thanks" in the last 24 h,
   and the suggestions are ready within 0.3 s (else checkout opens straight away).
   Data: sections/wp-upsell-base.liquid (the kit in the cart) + sections/wp-upsell-list.liquid (best sellers of its series),
   fetched in the background when the cart drawer renders. Mode: Theme settings > WP Cart > Checkout upsell
   (test = only after visiting any page with ?upsell=test; ?upsell=off to leave). Remembered in this browser only:
   localStorage wpco-skip (time of the last "No thanks"), sessionStorage wpco-shown (shown this visit). */
(() => {
  const C = window.wpCartConfig;
  if (!C || !C.upsell || C.upsell === 'off') return;
  // the new "Complete your order" step is being tested in this visit (assets/wp-complete-order.js): leave checkout to it
  try { if (/[?&]step=test/.test(location.search) || (sessionStorage.getItem('wpcs-test') === '1' && !/[?&]step=off/.test(location.search))) return; } catch (e) { /* storage blocked */ }
  let test = false;
  try {
    const q = new URLSearchParams(location.search).get('upsell');
    if (q === 'test') sessionStorage.setItem('wpco-test', '1');
    if (q === 'off') sessionStorage.removeItem('wpco-test');
    test = sessionStorage.getItem('wpco-test') === '1';
  } catch (e) { /* storage blocked */ }
  if (C.upsell !== 'on' && !test) return;

  const DAY = 864e5;
  const store = (fn) => { try { return fn(); } catch (e) { return null; } };
  const suppressed = () => !test && store(() => Date.now() - Number(localStorage.getItem('wpco-skip') || 0) < DAY || sessionStorage.getItem('wpco-shown') === '1');

  const money = (c) => new Intl.NumberFormat(C.locale || 'en', { style: 'currency', currency: C.currency || 'USD' }).format((c || 0) / 100);
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const tiers = String(C.tiers || '').split(',').map((t) => t.split(':').map(Number)).filter((t) => t[0] > 0 && t[1] > 0).sort((a, b) => a[0] - b[0]);
  const pct = (n) => tiers.reduce((p, [k, v]) => (n >= k ? v : p), 0);
  const kaching = (it) => { try { return JSON.parse(it.properties && it.properties.__kaching_bundles); } catch (e) { return null; } };
  const isCustom = (it) => /custom|personali/i.test(it.handle || '');
  // a kit that counts for Buy 2/3/4: PBN or diamond, not a tool inside a bundle, not Mystery / packs
  const isKit = (it) => {
    const k = kaching(it);
    return C.kitTypes.includes(it.product_type) && !(k && !k.main && !k.checkboxUpsell) && !/mystery|pack|bundle/i.test(it.handle || '');
  };
  const kitLines = (cart) => cart.items.filter(isKit);
  const kitCount = (cart) => kitLines(cart).reduce((n, it) => n + it.quantity, 0);
  const eligible = (cart) => {
    if (!cart || !cart.items || !cart.items.length || !tiers.length) return false;
    if (cart.items.some(isCustom)) return false;
    const n = kitCount(cart);
    return n >= 1 && n < 4;
  };

  // ----- national parks: state and region, to suggest parks near the one in the cart -----
  const PARKS = 'Acadia:ME,American Samoa:AS,Arches:UT,Badlands:SD,Big Bend:TX,Biscayne:FL,Black Canyon of the Gunnison:CO,Bryce Canyon:UT,Canyonlands:UT,Capitol Reef:UT,Carlsbad Caverns:NM,Channel Islands:CA,Congaree:SC,Crater Lake:OR,Cuyahoga Valley:OH,Death Valley:CA,Denali:AK,Dry Tortugas:FL,Everglades:FL,Gates of the Arctic:AK,Gateway Arch:MO,Glacier:MT,Glacier Bay:AK,Grand Canyon:AZ,Grand Teton:WY,Great Basin:NV,Great Sand Dunes:CO,Great Smoky Mountains:TN,Guadalupe Mountains:TX,Haleakala:HI,Hawai\'i Volcanoes:HI,Hot Springs:AR,Indiana Dunes:IN,Isle Royale:MI,Joshua Tree:CA,Katmai:AK,Kenai Fjords:AK,Kings Canyon:CA,Kobuk Valley:AK,Lake Clark:AK,Lassen Volcanic:CA,Mammoth Cave:KY,Mesa Verde:CO,Mount Rainier:WA,New River Gorge:WV,North Cascades:WA,Olympic:WA,Petrified Forest:AZ,Pinnacles:CA,Redwood:CA,Rocky Mountain:CO,Saguaro:AZ,Sequoia:CA,Shenandoah:VA,Theodore Roosevelt:ND,Virgin Islands:VI,Voyageurs:MN,White Sands:NM,Wind Cave:SD,Wrangell-St. Elias:AK,Yellowstone:WY,Yosemite:CA,Zion:UT';
  const STATE = Object.fromEntries(PARKS.split(',').map((p) => p.split(':')));
  const REGION = { CA: 'west', OR: 'west', WA: 'west', NV: 'southwest', UT: 'southwest', AZ: 'southwest', NM: 'southwest', TX: 'southwest', CO: 'rockies', WY: 'rockies', MT: 'rockies', AK: 'alaska', HI: 'pacific', AS: 'pacific', SD: 'midwest', ND: 'midwest', MN: 'midwest', MI: 'midwest', IN: 'midwest', OH: 'midwest', MO: 'midwest', FL: 'south', SC: 'south', TN: 'south', AR: 'south', KY: 'south', VA: 'south', WV: 'south', ME: 'northeast', VI: 'caribbean' };
  const STATE_NAME = { CA: 'California', UT: 'Utah', AK: 'Alaska', CO: 'Colorado', AZ: 'Arizona', FL: 'Florida', WA: 'Washington', TX: 'Texas', WY: 'Wyoming', HI: 'Hawaii', SD: 'South Dakota', NM: 'New Mexico' };
  const REGION_NAME = { west: 'West Coast', southwest: 'Southwest', rockies: 'Rocky Mountain', alaska: 'Alaska', pacific: 'Pacific', midwest: 'Midwest', south: 'Southern', northeast: 'Northeast', caribbean: 'Caribbean' };
  const first = (a) => (Array.isArray(a) ? a[0] : a) || '';

  // ----- data: fetched once per kit, in the background -----
  const cache = new Map(); // base handle -> Promise<{ base, list }>
  const sectionJson = async (url, attr) => {
    const r = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!r.ok) throw new Error(r.status);
    const m = new RegExp(`<script[^>]*${attr}[^>]*>([\\s\\S]*?)</script>`).exec(await r.text());
    return JSON.parse(m[1]);
  };
  const load = (handle) => {
    if (!cache.has(handle)) {
      const p = sectionJson(`/products/${encodeURIComponent(handle)}?section_id=wp-upsell-base`, 'data-wpco-base')
        .then((base) => sectionJson(`/collections/${encodeURIComponent(base.series)}?sort_by=best-selling&section_id=wp-upsell-list`, 'data-wpco-list').then((list) => ({ base, list })));
      p.ready = false;
      p.then((d) => { p.ready = true; p.data = d; }, () => { p.ready = true; p.data = null; });
      cache.set(handle, p);
    }
    return cache.get(handle);
  };
  let cssLoaded = false;
  const loadCss = () => {
    if (cssLoaded || !C.upsellCss) return;
    cssLoaded = true;
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = C.upsellCss;
    document.head.appendChild(l);
  };
  const prepare = (cart) => {
    if (!eligible(cart) || suppressed()) return;
    const run = () => { loadCss(); load(kitLines(cart)[0].handle); };
    (window.requestIdleCallback || ((f) => setTimeout(f, 300)))(run);
  };
  document.addEventListener('wp:cart-rendered', (e) => prepare(e.detail));

  // ----- choose the offer -----
  const plan = (cart, data) => {
    const { base, list } = data;
    const baseLine = kitLines(cart)[0];
    const inCart = new Set(cart.items.map((it) => it.handle));
    const places = new Set([first(base.place)]);
    list.forEach((p) => { if (inCart.has(p.h)) places.add(first(p.pl)); });
    places.delete('');
    const st = STATE[first(base.place)];
    const themes = new Set(base.theme || []);
    const baseOpts = JSON.stringify((baseLine.options_with_values || []).map((o) => o.value));
    const seen = new Set();
    const picks = list
      .map((p, i) => {
        if (inCart.has(p.h) || !p.v.length || places.has(first(p.pl))) return null;
        const s = STATE[first(p.pl)];
        let score = 0;
        if (st && s === st) score += 3; else if (st && s && REGION[s] === REGION[st]) score += 2;
        if ((p.th || []).some((t) => themes.has(t))) score += 2;
        if (base.difficulty && p.d === base.difficulty) score += 1;
        return { p, i, score, s };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score || a.i - b.i)
      .filter((c) => { const k = first(c.p.pl) || c.p.h; if (seen.has(k)) return false; seen.add(k); return true; })
      .slice(0, 3)
      .map(({ p, s }) => {
        const v = p.v.find((x) => JSON.stringify(x[1]) === baseOpts);
        const use = v || p.v[0];
        return { handle: p.h, title: p.t, image: p.i, id: use[0], price: use[2], meta: use[1].filter((o) => o !== 'Default Title').join(' · '), same: !!v, state: s };
      });
    if (!picks.length) return null;
    let sub = `Picked to go with your ${base.title}.`;
    if (st) {
      if (picks.every((x) => x.state === st)) sub = `More ${STATE_NAME[st] || ''} national parks to go with ${first(base.place)}.`.replace('More  ', 'More ');
      else if (picks.every((x) => x.state && REGION[x.state] === REGION[st])) sub = `More ${REGION_NAME[REGION[st]]} parks to go with ${first(base.place)}.`;
      else sub = `More national parks to go with ${first(base.place)}.`;
    }
    if (picks.every((x) => x.same) && baseOpts !== '["Default Title"]') sub += ' Same size and frame as yours.';
    const k = kaching(baseLine);
    const proTools = !!(k && k.main && k.bar) && cart.items.some((it) => { const c = kaching(it); return c && c.id === k.id && c.bundleProduct; });
    return { picks, sub, pro: proTools, baseLine };
  };

  // order total if the chosen kits are added: every kit line gets the better of its current price and the new step
  const estimate = (cart, added) => {
    const n = kitCount(cart) + added.length, p = pct(n);
    let total = 0, was = 0;
    cart.items.forEach((it) => {
      let line = it.final_line_price;
      if (p && isKit(it)) line = Math.min(line, it.original_line_price - Math.floor(it.original_line_price * p / 100));
      total += line;
      was += it.original_line_price;
    });
    added.forEach((o) => { total += o.price - Math.floor(o.price * p / 100); was += o.price; });
    return { n, p, total, saved: was - total };
  };

  // ----- popup -----
  let root = null, current = null, chosen = new Set(), busy = false;
  const build = () => {
    root = document.createElement('div');
    root.className = 'wpco';
    root.innerHTML = `
      <div class="wpco__dim" data-wpco-close></div>
      <div class="wpco__sheet" role="dialog" aria-modal="true" aria-labelledby="wpco-title" tabindex="-1">
        <span class="wpco__grab" aria-hidden="true"></span>
        <button type="button" class="wpco__x" data-wpco-close aria-label="Close and go to checkout">&times;</button>
        <div class="wpco__head">
          <div class="wpco__steps" data-wpco-steps></div>
          <p class="wpco__title" id="wpco-title" data-wpco-title></p>
          <p class="wpco__sub" data-wpco-sub></p>
        </div>
        <div class="wpco__offers" data-wpco-offers></div>
        <div class="wpco__foot">
          <p class="wpco__sum"><span>Order total <span class="wpco__saved" data-wpco-saved></span></span><b data-wpco-total></b></p>
          <button type="button" class="wpco__go" data-wpco-go></button>
          <button type="button" class="wpco__skip" data-wpco-skip>No thanks, go to checkout</button>
        </div>
      </div>`;
    document.body.appendChild(root);
    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-wpco-skip]')) return finish(false, true);
      if (e.target.closest('[data-wpco-close]')) return finish(true, true);
      if (e.target.closest('[data-wpco-go]')) return finish(true, false);
      const add = e.target.closest('[data-wpco-add]');
      if (add) {
        const h = add.dataset.wpcoAdd;
        if (chosen.has(h)) chosen.delete(h); else chosen.add(h);
        paint();
      }
    });
    root.addEventListener('keydown', (e) => { if (e.key === 'Escape') finish(true, true); });
    // phones: drag the sheet down to skip
    let y0 = null;
    const sheet = root.querySelector('.wpco__sheet');
    sheet.addEventListener('touchstart', (e) => { y0 = sheet.scrollTop <= 0 && !e.target.closest('.wpco__offers') ? e.touches[0].clientY : null; }, { passive: true });
    sheet.addEventListener('touchend', (e) => { if (y0 != null && e.changedTouches[0].clientY - y0 > 90) finish(true, true); y0 = null; }, { passive: true });
  };

  const paint = () => {
    const { cart, offer } = current;
    const added = offer.picks.filter((o) => chosen.has(o.handle));
    const est = estimate(cart, added);
    const n = est.n, next = n + 1, pn = pct(next);
    const $ = (s) => root.querySelector(s);
    $('[data-wpco-steps]').innerHTML = tiers.map(([k, v]) => `<span class="${k <= n ? 'is-done' : k === next ? 'is-next' : ''}">${k} kits · ${v}%</span>`).join('');
    let title;
    if (n >= tiers[tiers.length - 1][0]) title = `You've unlocked ${pct(n)}% off all ${n} kits`;
    else if (n === 1) title = `Add a 2nd kit, get ${pn}% off ${offer.baseLine.final_line_price < offer.baseLine.original_line_price ? 'it' : 'both'}`;
    else title = `Add 1 more kit, get ${pn}% off all ${next}`;
    $('[data-wpco-title]').textContent = title;
    $('[data-wpco-sub]').textContent = offer.sub + (offer.pro ? ' Your Pro brushes, Flow Aid and marker work for these too.' : '');
    $('[data-wpco-offers]').innerHTML = offer.picks.map((o) => {
      const on = chosen.has(o.handle);
      const p = pct(Math.max(n + (on ? 0 : 1), 2));
      const now = o.price - Math.floor(o.price * p / 100);
      return `<div class="wpco__card">
        <span class="wpco__img">${o.image ? `<img src="${esc(o.image)}" alt="" width="300" height="375" loading="eager">` : ''}</span>
        <span class="wpco__name">${esc(o.title)}</span>
        ${o.meta ? `<span class="wpco__meta">${esc(o.meta)}</span>` : ''}
        <span class="wpco__price"><b>${money(now)}</b>${now < o.price ? `<s>${money(o.price)}</s>` : ''}</span>
        <button type="button" class="wpco__add${on ? ' is-on' : ''}" data-wpco-add="${esc(o.handle)}" aria-pressed="${on}">${on ? 'Added ✓' : 'Add'}</button>
      </div>`;
    }).join('');
    $('[data-wpco-saved]').textContent = est.saved > 0 ? `· you save ${money(est.saved)}` : '';
    $('[data-wpco-total]').textContent = money(added.length ? est.total : cart.total_price);
    $('[data-wpco-go]').textContent = added.length ? `Add ${added.length > 1 ? `${added.length} kits` : 'kit'} & checkout · ${money(est.total)}` : `Checkout · ${money(cart.total_price)}`;
  };

  const show = (cart, offer) => {
    if (!root) build();
    current = { cart, offer };
    chosen = new Set();
    paint();
    root.classList.add('is-open');
    document.documentElement.classList.add('wpco-open');
    setTimeout(() => root.querySelector('.wpco__sheet').focus(), 30);
    store(() => sessionStorage.setItem('wpco-shown', '1'));
    // count how often it is shown (cart attribute, hidden at checkout)
    const f = (window.wpCart && window.wpCart.fetch) || fetch;
    f('/cart/update.js', { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { _upsell: 'shown' } }) }).catch(() => {});
  };

  const go = () => { location.href = C.checkoutUrl || '/checkout'; };
  const finish = async (withChosen, skip) => {
    if (busy) return;
    const added = withChosen ? current.offer.picks.filter((o) => chosen.has(o.handle)) : [];
    if (skip && !added.length) store(() => localStorage.setItem('wpco-skip', String(Date.now())));
    if (!added.length) { go(); return; }
    busy = true;
    const btn = root.querySelector('[data-wpco-go]');
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      const f = (window.wpCart && window.wpCart.fetch) || fetch;
      await f('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: added.map((o) => ({ id: o.id, quantity: 1, properties: { _upsell: 'checkout-popup' } })) }),
      });
    } catch (e) { /* checkout anyway with what the cart has */ }
    go();
  };

  // ----- the cart we decide on: the drawer's last render, re-read when something changed it since (e.g. the cart page) -----
  let fresh = null, dirty = false;
  document.addEventListener('wp:cart-rendered', (e) => { fresh = e.detail; dirty = false; });
  const isCartWrite = (url, method) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url)) && String(method || 'GET').toUpperCase() === 'POST';
  const pf = window.fetch;
  window.fetch = function (input, init) {
    if (isCartWrite(typeof input === 'string' ? input : (input && input.url) || '', (init && init.method) || (input && input.method))) dirty = true;
    return pf.apply(this, arguments);
  };
  const px = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) { if (isCartWrite(url, method)) dirty = true; return px.apply(this, arguments); };
  const currentCart = async () => {
    const known = fresh || (window.wpCart && window.wpCart.getCart && window.wpCart.getCart());
    if (known && !dirty) return known;
    const f = (window.wpCart && window.wpCart.fetch) || pf;
    return f('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  };
  const within = (promise, ms) => Promise.race([promise.then((v) => ({ v }), () => null), new Promise((r) => setTimeout(() => r(null), ms))]);

  // ----- intercept Checkout (drawer button and cart page button; express buttons are left alone) -----
  let waiting = false;
  window.addEventListener('click', (e) => {
    const btn = e.target.closest('.wpc__checkout, #checkout, button[name="checkout"], input[name="checkout"]');
    if (!btn || e.metaKey || e.ctrlKey || waiting) return;
    if (root && root.contains(btn)) return;
    if (suppressed()) return;
    const known = fresh || (window.wpCart && window.wpCart.getCart && window.wpCart.getCart());
    if (known && !dirty && !eligible(known)) return; // nothing to offer: normal checkout, no delay
    e.preventDefault();
    e.stopImmediatePropagation();
    waiting = true;
    btn.setAttribute('aria-busy', 'true');
    const t0 = Date.now();
    (async () => {
      const got = await within(currentCart(), 400);
      const cart = got && got.v;
      if (!eligible(cart)) return go();
      const p = load(kitLines(cart)[0].handle);
      if (!p.ready) await within(p, Math.max(300 - (Date.now() - t0), 0));
      const offer = p.ready && p.data ? plan(cart, p.data) : null;
      if (offer) show(cart, offer); else go();
    })().catch(go).finally(() => { waiting = false; btn.removeAttribute('aria-busy'); });
  }, true);

  // back from checkout: the browser may restore this page as it was (popup open, button on "Adding…")
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    waiting = false;
    busy = false;
    dirty = true;
    document.documentElement.classList.remove('wpco-open');
    if (root) {
      root.classList.remove('is-open');
      const btn = root.querySelector('[data-wpco-go]');
      btn.disabled = false;
    }
  });

  // the cart page has no drawer render before the click: prepare as soon as the cart is known
  const warm = () => { const c = window.wpCart && window.wpCart.getCart && window.wpCart.getCart(); if (c) prepare(c); };
  window.addEventListener('load', () => setTimeout(warm, 800));
  window.wpUpsell = { load, plan, estimate, test };
})();
